import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart, LineChart, PieChart, TrendingUp, DollarSign, Package, CalendarDays, Download, Eye, Users, Folder, ShoppingBag, Loader } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { generatePdfReport } from '../../utils/pdfGenerator';
import { generateExcelReport } from '../../utils/excelGenerator';
import apiService from '../../utils/apiService';

const mockSalesData = [
  { month: 'Ene', sales: 12000, orders: 150, products: 500 },
  { month: 'Feb', sales: 15000, orders: 180, products: 600 },
  { month: 'Mar', sales: 13000, orders: 160, products: 550 },
  { month: 'Abr', sales: 17000, orders: 200, products: 700 },
  { month: 'May', sales: 19000, orders: 220, products: 750 },
  { month: 'Jun', sales: 22000, orders: 250, products: 800 },
  { month: 'Jul', sales: 20000, orders: 230, products: 720 },
  { month: 'Ago', sales: 24000, orders: 270, products: 850 },
  { month: 'Sep', sales: 21000, orders: 240, products: 780 },
  { month: 'Oct', sales: 26000, orders: 290, products: 900 },
  { month: 'Nov', sales: 28000, orders: 310, products: 950 },
  { month: 'Dic', sales: 30000, orders: 330, products: 1000 },
];

const mockProductVisits = [
  { name: 'Hoteles Patagonia 2025 - 2026', visits: 1250, sales: 150 },
  { name: 'Hoteles Sur 2025 - 2026', visits: 980, sales: 75 },
  { name: 'Hoteles Santiago 2025 - 2026', visits: 720, sales: 200 },
  { name: 'Aspiradora Robot', visits: 600, sales: 90 },
  { name: 'Bicicleta Montaña Pro', visits: 550, sales: 45 },
  { name: 'Reloj Inteligente Sport', visits: 480, sales: 110 },
];

const mockCategoryVisits = [
  { name: 'Hoteleria Chile', visits: 3500, productsViewed: 1500 },
  { name: 'Servicios Turísticos', visits: 1800, productsViewed: 700 },
  { name: 'All Inclusive', visits: 1200, productsViewed: 500 },
  { name: 'Turismo de Nieve', visits: 900, productsViewed: 300 },
];

const mockAgencyAccess = [
  { name: 'Agencia Viajes Pro', accesses: 520, lastLogin: '2024-07-20' },
  { name: 'Operador Elite', accesses: 410, lastLogin: '2024-07-19' },
  { name: 'Agencia Aventuras XYZ', accesses: 300, lastLogin: '2024-07-18' },
  { name: 'Turismo Global Solutions', accesses: 250, lastLogin: '2024-07-17' },
];

// Datos de evolución de productos (ya no se usan)
// const mockProductEvolution = [];

const DownloadLineChart = ({ data }) => {
  const [tooltip, setTooltip] = React.useState(null);

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-3">
        <svg className="w-14 h-14 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
        <p>No hay datos de evolución disponibles</p>
      </div>
    );
  }

  const W = 800, H = 260;
  const padL = 48, padR = 20, padT = 20, padB = 38;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const counts = data.map(d => Number(d.count));
  const maxVal = Math.max(...counts, 1);
  const n = data.length;

  const points = data.map((d, i) => {
    const dt = new Date(d.date);
    return {
      x: padL + (n === 1 ? chartW / 2 : (i / (n - 1)) * chartW),
      y: padT + chartH - (Number(d.count) / maxVal) * chartH,
      count: Number(d.count),
      label: `${dt.getDate()}/${dt.getMonth() + 1}`,
      fullDate: dt.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })
    };
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${points[n - 1].x.toFixed(1)},${(padT + chartH).toFixed(1)} L${points[0].x.toFixed(1)},${(padT + chartH).toFixed(1)} Z`;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    val: Math.round(maxVal * f),
    y: padT + chartH - f * chartH
  }));

  const showEvery = Math.max(1, Math.ceil(n / 9));

  return (
    <div className="relative w-full h-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full">
        <defs>
          <linearGradient id="dlAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Ejes */}
        <line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke="#e5e7eb" strokeWidth="1" />
        <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="#e5e7eb" strokeWidth="1" />

        {/* Grid + Y labels */}
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={t.y} x2={W - padR} y2={t.y} stroke="#f3f4f6" strokeWidth={i === 0 ? 0 : 1} />
            <text x={padL - 6} y={t.y + 4} textAnchor="end" style={{ fontSize: 11, fill: '#9ca3af' }}>{t.val}</text>
          </g>
        ))}

        {/* Área */}
        <path d={areaPath} fill="url(#dlAreaGrad)" />

        {/* Línea */}
        <path d={linePath} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

        {/* X labels */}
        {points.map((p, i) => i % showEvery === 0 && (
          <text key={i} x={p.x} y={H - 4} textAnchor="middle" style={{ fontSize: 11, fill: '#6b7280' }}>{p.label}</text>
        ))}

        {/* Puntos interactivos */}
        {points.map((p, i) => {
          const active = tooltip && tooltip.i === i;
          return (
            <g key={i} onMouseEnter={() => setTooltip({ ...p, i })} onMouseLeave={() => setTooltip(null)} style={{ cursor: 'pointer' }}>
              <circle cx={p.x} cy={p.y} r={14} fill="transparent" />
              <circle cx={p.x} cy={p.y} r={active ? 6 : 4} fill={active ? '#1d4ed8' : '#3b82f6'} stroke="white" strokeWidth="2" />
            </g>
          );
        })}

        {/* Tooltip */}
        {tooltip && (() => {
          const tx = Math.min(Math.max(tooltip.x - 54, padL), W - padR - 108);
          const ty = Math.max(tooltip.y - 52, padT);
          return (
            <g>
              <rect x={tx} y={ty} width={108} height={40} rx={7} fill="#1e40af" opacity={0.93} />
              <text x={tx + 54} y={ty + 15} textAnchor="middle" style={{ fontSize: 11, fill: '#bfdbfe' }}>{tooltip.fullDate}</text>
              <text x={tx + 54} y={ty + 32} textAnchor="middle" style={{ fontSize: 13, fill: 'white', fontWeight: 'bold' }}>{tooltip.count} descargas</text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
};

const parseUserAgent = (ua) => {
  if (!ua) return 'N/A';
  const isMobile = /mobile|android|iphone|ipad|tablet/i.test(ua);
  let browser = 'Desconocido';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/OPR\//i.test(ua) || /Opera/i.test(ua)) browser = 'Opera';
  else if (/SamsungBrowser/i.test(ua)) browser = 'Samsung';
  else if (/Chrome\//i.test(ua)) browser = 'Chrome';
  else if (/Safari\//i.test(ua) && /Version\//i.test(ua)) browser = 'Safari';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';
  else if (/MSIE|Trident/i.test(ua)) browser = 'IE';
  return isMobile ? `📱 ${browser} (Móvil)` : `🖥️ ${browser}`;
};

const SalesStatisticsPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('yearly');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalCategories: 0,
    totalUsers: 0,
    recentProducts: [],
    categoriesWithCount: [],
    recentUsers: []
  });
  const [downloadStats, setDownloadStats] = useState({
    mostDownloaded: [],
    recentDownloads: [],
    totalDownloads: 0,
    pagination: { page: 1, limit: 10, totalPages: 1, hasMore: false }
  });
  const [downloadEvolution, setDownloadEvolution] = useState([]);
  const [evolutionLoading, setEvolutionLoading] = useState(false);
  const [downloadPage, setDownloadPage] = useState(1);
  const [downloadPeriod, setDownloadPeriod] = useState({ type: 'days', value: 30 });
  const [exporting, setExporting] = useState(false);

  // Agrega datos por semana o mes según el período
  const aggregateEvolution = (rawData, tf) => {
    if (!rawData || rawData.length === 0) return [];
    if (tf === 'monthly') return rawData; // diario, sin agrupar

    const groups = {};
    rawData.forEach(item => {
      const d = new Date(item.date);
      let key;
      if (tf === 'quarterly') {
        // Agrupar por semana: inicio de la semana
        const day = d.getDay();
        const start = new Date(d);
        start.setDate(d.getDate() - day);
        key = start.toISOString().slice(0, 10);
      } else {
        // Anual: agrupar por mes
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      }
      groups[key] = (groups[key] || 0) + Number(item.count);
    });

    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count }));
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Cargar estadísticas generales
        try {
          const statsResponse = await apiService.getStats();
          if (statsResponse.success) {
            setStats({
              totalProducts: statsResponse.stats.totalProducts,
              totalCategories: statsResponse.stats.totalCategories,
              totalUsers: statsResponse.stats.totalUsers,
              recentProducts: statsResponse.stats.recentProducts || [],
              categoriesWithCount: statsResponse.stats.categoriesWithCount || [],
              recentUsers: statsResponse.stats.recentUsers || []
            });
          }
        } catch (error) {
          console.error('Error cargando estadísticas generales:', error);
        }

        // Cargar estadísticas de descargas
        try {
          const days = downloadPeriod.type === 'days' ? downloadPeriod.value : null;
          const months = downloadPeriod.type === 'months' ? downloadPeriod.value : null;
          const downloadsResponse = await apiService.getDownloadStats(days, 10, downloadPage, months);
          if (downloadsResponse.success) {
            setDownloadStats({
              mostDownloaded: downloadsResponse.stats.mostDownloaded || [],
              recentDownloads: downloadsResponse.stats.recentDownloads || [],
              totalDownloads: downloadsResponse.stats.totalDownloads || 0,
              pagination: downloadsResponse.stats.pagination || { page: 1, limit: 10, totalPages: 1, hasMore: false }
            });
          }
        } catch (error) {
          console.error('Error cargando estadísticas de descargas:', error);
        }
      } catch (error) {
        console.error('Error general cargando estadísticas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    
    // Escuchar cambios de tenant
    window.addEventListener('tenantChanged', fetchStats);
    return () => window.removeEventListener('tenantChanged', fetchStats);
  }, [downloadPage, downloadPeriod]);

  // Efecto separado para evolución — reacciona solo a cambios de timeframe
  useEffect(() => {
    const fetchEvolution = async () => {
      setEvolutionLoading(true);
      try {
        const evolutionDays = timeframe === 'yearly' ? 365 : timeframe === 'quarterly' ? 90 : 30;
        const res = await apiService.getDownloadEvolution(evolutionDays);
        if (res.success) {
          setDownloadEvolution(aggregateEvolution(res.evolution || [], timeframe));
        }
      } catch (error) {
        console.error('Error cargando evolución de descargas:', error);
      } finally {
        setEvolutionLoading(false);
      }
    };
    fetchEvolution();
  }, [timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Funciones para manejar paginación
  const handleNextPage = () => {
    if (downloadStats.pagination.hasMore) {
      setDownloadPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (downloadPage > 1) {
      setDownloadPage(prev => prev - 1);
    }
  };

  const handlePeriodChange = (type, value) => {
    setDownloadPeriod({ type, value });
    setDownloadPage(1); // Reset a página 1 al cambiar período
  };

  if (!isAdmin) {
    navigate('/admin');
    return null;
  }

  const kpiCards = [
    { title: 'Servicios/Hoteles', value: stats.totalProducts.toLocaleString(), icon: Package, color: 'text-green-600', bg: 'bg-green-100' },
    { title: 'Categorías Turísticas', value: stats.totalCategories.toLocaleString(), icon: Folder, color: 'text-blue-600', bg: 'bg-blue-100' },
    { title: 'Usuarios/Agencias', value: stats.totalUsers.toLocaleString(), icon: Users, color: 'text-purple-600', bg: 'bg-purple-100' },
  ];

  const exportProductVisitsPdf = () => {
    const headers = ['Producto', 'Categoría', 'Última Actualización'];
    const data = stats.recentProducts.map(p => [
      p.name, 
      p.category?.name || 'Sin categoría', 
      new Date(p.updated_at).toLocaleDateString('es-ES')
    ]);
    generatePdfReport('Reporte de Productos Recientes', headers, data, 'productos_recientes');
  };

  const exportProductVisitsExcel = () => {
    const headers = ['Producto', 'Categoría', 'Última Actualización'];
    const data = stats.recentProducts.map(p => [
      p.name, 
      p.category?.name || 'Sin categoría', 
      new Date(p.updated_at).toLocaleDateString('es-ES')
    ]);
    generateExcelReport('Reporte de Productos Recientes', headers, data, 'productos_recientes');
  };

  const exportCategoryVisitsPdf = () => {
    const headers = ['Categoría', 'Cantidad de Productos'];
    const data = stats.categoriesWithCount.map(c => [c.name, c.productCount]);
    generatePdfReport('Reporte de Categorías con Productos', headers, data, 'categorias_productos');
  };

  const exportCategoryVisitsExcel = () => {
    const headers = ['Categoría', 'Cantidad de Productos'];
    const data = stats.categoriesWithCount.map(c => [c.name, c.productCount]);
    generateExcelReport('Reporte de Categorías con Productos', headers, data, 'categorias_productos');
  };

  const exportAgencyAccessPdf = () => {
    const headers = ['Usuario', 'Email', 'Agencia', 'Último Acceso'];
    const data = stats.recentUsers.map(u => [
      u.name, 
      u.email, 
      u.agency || 'N/A', 
      u.last_login ? new Date(u.last_login).toLocaleString('es-ES') : 'Nunca'
    ]);
    generatePdfReport('Reporte de Accesos de Usuarios', headers, data, 'accesos_usuarios');
  };

  const exportAgencyAccessExcel = () => {
    const headers = ['Usuario', 'Email', 'Agencia', 'Último Acceso'];
    const data = stats.recentUsers.map(u => [
      u.name, 
      u.email, 
      u.agency || 'N/A', 
      u.last_login ? new Date(u.last_login).toLocaleString('es-ES') : 'Nunca'
    ]);
    generateExcelReport('Reporte de Accesos de Usuarios', headers, data, 'accesos_usuarios');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader className="w-12 h-12 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Estadísticas de Turismo y Hotelería</h2>
          <p className="text-xl text-gray-600">Análisis de reservas, servicios y actividad turística.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {kpiCards.map((card, index) => (
            <motion.div
              key={index}
              className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20 flex items-center gap-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${card.bg}`}>
                <card.icon className={`w-7 h-7 ${card.color}`} />
              </div>
              <div>
                <p className="text-gray-500 text-sm font-medium">{card.title}</p>
                <h3 className="text-3xl font-bold text-gray-900">{card.value}</h3>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">
              Evolución de Descargas ({timeframe === 'yearly' ? 'Último año' : timeframe === 'quarterly' ? 'Últimos 3 meses' : 'Últimos 30 días'})
            </h3>
            <div className="flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setTimeframe('monthly')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${timeframe === 'monthly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Mensual
              </button>
              <button
                onClick={() => setTimeframe('quarterly')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${timeframe === 'quarterly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Trimestral
              </button>
              <button
                onClick={() => setTimeframe('yearly')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${timeframe === 'yearly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Anual
              </button>
            </div>
          </div>
          
          {/* Gráfico SVG de línea */}
          <div className="h-80 bg-gray-50 rounded-xl border border-gray-200 px-4 pt-4 pb-2">
            {evolutionLoading ? (
              <div className="h-full flex items-center justify-center">
                <Loader className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : (
              <DownloadLineChart data={downloadEvolution} />
            )}
          </div>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {/* Reporte de Productos Más Visitados */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Servicios/Hoteles Recientes</h3>
              <div className="flex gap-2">
                <motion.button
                  onClick={exportProductVisitsPdf}
                  className="p-2 bg-red-100 rounded-full text-red-600 hover:bg-red-200 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar PDF"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={exportProductVisitsExcel}
                  className="p-2 bg-green-100 rounded-full text-green-600 hover:bg-green-200 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar Excel"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
            <ul className="space-y-3">
              {stats.recentProducts.length > 0 ? (
                stats.recentProducts.slice(0, 6).map((product, index) => (
                  <li key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-gray-800">{product.name}</span>
                    </div>
                    <span className="text-gray-600 text-sm">{new Date(product.updated_at).toLocaleDateString('es-ES')}</span>
                  </li>
                ))
              ) : (
                <li className="text-center text-gray-500 py-4">No hay productos disponibles</li>
              )}
            </ul>
          </div>

          {/* Reporte de Categorías Más Visitadas */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Categorías con Más Productos</h3>
              <div className="flex gap-2">
                <motion.button
                  onClick={exportCategoryVisitsPdf}
                  className="p-2 bg-red-100 rounded-full text-red-600 hover:bg-red-200 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar PDF"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={exportCategoryVisitsExcel}
                  className="p-2 bg-green-100 rounded-full text-green-600 hover:bg-green-200 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar Excel"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
            <ul className="space-y-3">
              {stats.categoriesWithCount.length > 0 ? (
                stats.categoriesWithCount.map((category, index) => (
                  <li key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Folder className="w-4 h-4 text-purple-500" />
                      <span className="font-medium text-gray-800">{category.name}</span>
                    </div>
                    <span className="text-gray-600">{category.productCount} productos</span>
                  </li>
                ))
              ) : (
                <li className="text-center text-gray-500 py-4">No hay categorías disponibles</li>
              )}
            </ul>
          </div>

          {/* Reporte de Usuarios/Agencias con Más Acceso */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20 md:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Productos Más Descargados</h3>
              <div className="flex gap-2">
                <motion.button
                  onClick={() => {
                    const headers = ['Producto', 'Categoría', 'Total Descargas'];
                    const data = downloadStats.mostDownloaded.map(item => [
                      item.product?.name || 'Desconocido',
                      item.product?.category?.name || 'Sin categoría',
                      item.download_count || 0
                    ]);
                    generatePdfReport('Productos Más Descargados', headers, data, 'productos_mas_descargados');
                  }}
                  className="p-2 bg-red-100 rounded-full text-red-600 hover:bg-red-200 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar PDF"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={() => {
                    const headers = ['Producto', 'Categoría', 'Total Descargas'];
                    const data = downloadStats.mostDownloaded.map(item => [
                      item.product?.name || 'Desconocido',
                      item.product?.category?.name || 'Sin categoría',
                      item.download_count || 0
                    ]);
                    generateExcelReport('Productos Más Descargados', headers, data, 'productos_mas_descargados');
                  }}
                  className="p-2 bg-green-100 rounded-full text-green-600 hover:bg-green-200 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar Excel"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
            <ul className="space-y-3">
              {downloadStats.mostDownloaded.length > 0 ? (
                downloadStats.mostDownloaded.map((item, index) => (
                  <li key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white font-bold text-sm">
                        {index + 1}
                      </span>
                      <Download className="w-4 h-4 text-green-500" />
                      <div>
                        <span className="font-medium text-gray-800">{item.product?.name || 'Producto desconocido'}</span>
                        {item.product?.category?.name && (
                          <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            {item.product.category.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-blue-600 font-semibold">{item.download_count || 0} descargas</span>
                  </li>
                ))
              ) : (
                <li className="text-center text-gray-500 py-4">No hay estadísticas de descargas disponibles</li>
              )}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">Total de descargas: <span className="font-bold text-blue-600">{downloadStats.totalDownloads}</span></p>
            </div>
          </div>

          {/* Descargas Recientes Detalladas */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20 md:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Historial de Descargas Recientes</h3>
              <div className="flex gap-2 items-center">
                {/* Selector de período */}
                <select
                  value={downloadPeriod.type === 'days' ? `days-${downloadPeriod.value}` : `months-${downloadPeriod.value}`}
                  onChange={(e) => {
                    const [type, value] = e.target.value.split('-');
                    handlePeriodChange(type, parseInt(value));
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="days-1">Hoy (1 día)</option>
                  <option value="days-7">Últimos 7 días</option>
                  <option value="days-30">Últimos 30 días</option>
                  <option value="months-3">Últimos 3 meses</option>
                  <option value="months-6">Últimos 6 meses</option>
                  <option value="months-12">Último año</option>
                </select>
                <motion.button
                  onClick={async () => {
                    if (exporting) return;
                    setExporting(true);
                    try {
                      const days = downloadPeriod.type === 'days' ? downloadPeriod.value : null;
                      const months = downloadPeriod.type === 'months' ? downloadPeriod.value : null;
                      const res = await apiService.getDownloadStatsAll({ days, months });
                      const items = res.success ? res.downloads : downloadStats.recentDownloads;
                      const headers = ['Fecha/Hora', 'Producto', 'Categoría', 'Documento', 'Usuario', 'IP', 'Navegador'];
                      const data = items.map(item => [
                        new Date(item.download_date).toLocaleString('es-CL'),
                        item.product?.name || 'Desconocido',
                        item.product?.category?.name || 'Sin categoría',
                        item.document_name || 'N/A',
                        item.user?.name || item.user?.email || 'Usuario anónimo',
                        item.ip_address || 'N/A',
                        parseUserAgent(item.user_agent)
                      ]);
                      generatePdfReport('Historial de Descargas', headers, data, 'historial_descargas');
                    } finally {
                      setExporting(false);
                    }
                  }}
                  disabled={exporting}
                  className="p-2 bg-red-100 rounded-full text-red-600 hover:bg-red-200 transition-colors disabled:opacity-60"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar PDF"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={async () => {
                    if (exporting) return;
                    setExporting(true);
                    try {
                      const days = downloadPeriod.type === 'days' ? downloadPeriod.value : null;
                      const months = downloadPeriod.type === 'months' ? downloadPeriod.value : null;
                      const res = await apiService.getDownloadStatsAll({ days, months });
                      const items = res.success ? res.downloads : downloadStats.recentDownloads;
                      const headers = ['Fecha/Hora', 'Producto', 'Categoría', 'Documento', 'Usuario', 'IP', 'Navegador'];
                      const data = items.map(item => [
                        new Date(item.download_date).toLocaleString('es-CL'),
                        item.product?.name || 'Desconocido',
                        item.product?.category?.name || 'Sin categoría',
                        item.document_name || 'N/A',
                        item.user?.name || item.user?.email || 'Usuario anónimo',
                        item.ip_address || 'N/A',
                        parseUserAgent(item.user_agent)
                      ]);
                      generateExcelReport('Historial de Descargas', headers, data, 'historial_descargas');
                    } finally {
                      setExporting(false);
                    }
                  }}
                  disabled={exporting}
                  className="p-2 bg-green-100 rounded-full text-green-600 hover:bg-green-200 transition-colors disabled:opacity-60"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Descargar Excel"
                >
                  <Download className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Fecha/Hora</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Producto</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Documento</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Usuario</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">IP</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Navegador</th>
                  </tr>
                </thead>
                <tbody>
                  {downloadStats.recentDownloads.length > 0 ? (
                    downloadStats.recentDownloads.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-gray-600">
                          {new Date(item.download_date).toLocaleString('es-CL', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-gray-800">{item.product?.name || 'Producto desconocido'}</span>
                          {item.product?.category?.name && (
                            <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                              {item.product.category.name}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {item.document_name || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {item.user?.name || item.user?.email || 'Anónimo'}
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs font-mono">
                          {item.ip_address || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs" title={item.user_agent || 'N/A'}>
                          {parseUserAgent(item.user_agent)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-gray-500 py-8">
                        No hay descargas recientes registradas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Controles de paginación */}
            {downloadStats.recentDownloads.length > 0 && (
              <div className="mt-4 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Página {downloadStats.pagination.page} de {downloadStats.pagination.totalPages}
                  {' • '}
                  {downloadStats.pagination.totalRecent} descargas en el período
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={downloadPage === 1}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!downloadStats.pagination.hasMore}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SalesStatisticsPage;